import type { AvatarConfig } from '@qq/shared';
import { INK, backgroundOf, hairOf, outfitOf, skinOf } from './palette.js';

/**
 * The drawn character. Original flat-vector art: bold outlines, big eyes, no
 * traced likeness of anything licensed.
 *
 * Everything is drawn in a 0 0 200 200 box so an avatar can be dropped in at
 * any size - 44px in a list, 240px in the builder - without touching the paths.
 */

interface AvatarProps {
  config: AvatarConfig;
  size?: number;
  /** A selfie to show instead of the drawn face, in the same round frame. */
  photoUrl?: string | null;
  title?: string;
  className?: string;
}

export function Avatar({ config, size = 120, photoUrl, title, className }: AvatarProps) {
  const skin = skinOf(config);
  const hair = hairOf(config);
  const [bgTop, bgBottom] = backgroundOf(config);
  const outfit = outfitOf(config);
  const id = `av-${config.background}-${config.skin}`;

  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={title ?? 'Character'}
    >
      <defs>
        <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={bgTop} />
          <stop offset="100%" stopColor={bgBottom} />
        </linearGradient>
        <clipPath id={`${id}-clip`}>
          <circle cx="100" cy="100" r="96" />
        </clipPath>
      </defs>

      <circle cx="100" cy="100" r="96" fill={`url(#${id}-bg)`} />

      <g clipPath={`url(#${id}-clip)`}>
        {photoUrl ? (
          <image
            href={photoUrl}
            x="0"
            y="0"
            width="200"
            height="200"
            preserveAspectRatio="xMidYMid slice"
          />
        ) : (
          <DrawnFigure config={config} skin={skin} hair={hair} outfit={outfit} />
        )}
      </g>

      <circle
        cx="100"
        cy="100"
        r="94"
        fill="none"
        stroke={INK}
        strokeOpacity="0.15"
        strokeWidth="4"
      />
    </svg>
  );
}

function DrawnFigure({
  config,
  skin,
  hair,
  outfit,
}: {
  config: AvatarConfig;
  skin: { base: string; shade: string };
  hair: string;
  outfit: { base: string; trim: string };
}) {
  return (
    <>
      {/* Shoulders, drawn before the head so the neck tucks behind. */}
      <path d="M42 200v-24a58 58 0 0 1 116 0v24Z" fill={outfit.base} />
      <Outfit kind={config.outfit} outfit={outfit} />

      {/* Neck */}
      <rect x="88" y="118" width="24" height="26" rx="12" fill={skin.shade} />

      {/* Hair behind the head */}
      <HairBack style={config.hair} colour={hair} />

      {/* Head */}
      <ellipse cx="100" cy="88" rx="42" ry="45" fill={skin.base} />
      <path
        d="M100 133c-14 0-26-8-32-20 8 5 20 8 32 8s24-3 32-8c-6 12-18 20-32 20Z"
        fill={skin.shade}
        opacity="0.45"
      />

      {/* Ears */}
      <circle cx="57" cy="90" r="8" fill={skin.base} />
      <circle cx="143" cy="90" r="8" fill={skin.base} />

      <Eyes style={config.eyes} />
      <Mouth style={config.mouth} />

      {/* Cheeks */}
      <circle cx="72" cy="100" r="7" fill="#e89684" opacity="0.4" />
      <circle cx="128" cy="100" r="7" fill="#e89684" opacity="0.4" />

      <HairFront style={config.hair} colour={hair} />
      <Accessory kind={config.accessory} />
    </>
  );
}

function HairBack({ style, colour }: { style: string; colour: string }) {
  switch (style) {
    case 'ponytail':
      return (
        <>
          <ellipse cx="100" cy="84" rx="46" ry="48" fill={colour} />
          <path d="M146 74c18 4 26 22 22 40-3 14-14 22-24 20 8-16 8-42 2-60Z" fill={colour} />
        </>
      );
    case 'bunches':
      return (
        <>
          <ellipse cx="100" cy="84" rx="46" ry="48" fill={colour} />
          <circle cx="48" cy="72" r="16" fill={colour} />
          <circle cx="152" cy="72" r="16" fill={colour} />
        </>
      );
    case 'braids':
      return (
        <>
          <ellipse cx="100" cy="84" rx="46" ry="48" fill={colour} />
          <path d="M56 92c-10 16-10 34-4 48 6-2 10-6 12-12Z" fill={colour} />
          <path d="M144 92c10 16 10 34 4 48-6-2-10-6-12-12Z" fill={colour} />
        </>
      );
    case 'wavy':
      return <ellipse cx="100" cy="86" rx="50" ry="50" fill={colour} />;
    case 'curls':
      return (
        <>
          <circle cx="68" cy="62" r="20" fill={colour} />
          <circle cx="100" cy="52" r="22" fill={colour} />
          <circle cx="132" cy="62" r="20" fill={colour} />
          <circle cx="58" cy="86" r="16" fill={colour} />
          <circle cx="142" cy="86" r="16" fill={colour} />
        </>
      );
    case 'spikes':
      return (
        <>
          <ellipse cx="100" cy="84" rx="45" ry="46" fill={colour} />
          <path d="M58 62 70 26l14 26 16-32 16 32 14-26 12 36Z" fill={colour} />
        </>
      );
    default:
      return <ellipse cx="100" cy="84" rx="45" ry="46" fill={colour} />;
  }
}

function HairFront({ style, colour }: { style: string; colour: string }) {
  switch (style) {
    case 'buzz':
      return <path d="M58 82a42 42 0 0 1 84 0c-6-14-24-22-42-22s-36 8-42 22Z" fill={colour} />;
    case 'curls':
      return (
        <path
          d="M60 80c6-16 22-26 40-26s34 10 40 26c-10-8-24-12-40-12s-30 4-40 12Z"
          fill={colour}
        />
      );
    case 'wavy':
      return (
        <path
          d="M58 80c4-10 10-18 20-14 8 3 10 10 22 10s14-7 22-10c10-4 16 4 20 14-8-18-26-30-42-30S66 62 58 80Z"
          fill={colour}
        />
      );
    default:
      return (
        <path
          d="M58 80c2-20 20-34 42-34s40 14 42 34c-8-16-24-24-42-24s-34 8-42 24Z"
          fill={colour}
        />
      );
  }
}

function Eyes({ style }: { style: string }) {
  const white = '#ffffff';
  switch (style) {
    case 'happy':
      return (
        <>
          <path
            d="M74 84a10 10 0 0 1 20 0"
            fill="none"
            stroke={INK}
            strokeWidth="5"
            strokeLinecap="round"
          />
          <path
            d="M106 84a10 10 0 0 1 20 0"
            fill="none"
            stroke={INK}
            strokeWidth="5"
            strokeLinecap="round"
          />
        </>
      );
    case 'wide':
      return (
        <>
          <circle cx="84" cy="86" r="11" fill={white} stroke={INK} strokeWidth="3" />
          <circle cx="116" cy="86" r="11" fill={white} stroke={INK} strokeWidth="3" />
          <circle cx="84" cy="87" r="5" fill={INK} />
          <circle cx="116" cy="87" r="5" fill={INK} />
        </>
      );
    case 'wink':
      return (
        <>
          <path
            d="M74 86a10 10 0 0 1 20 0"
            fill="none"
            stroke={INK}
            strokeWidth="5"
            strokeLinecap="round"
          />
          <circle cx="116" cy="86" r="6" fill={INK} />
        </>
      );
    case 'sparkle':
      return (
        <>
          <circle cx="84" cy="86" r="8" fill={INK} />
          <circle cx="116" cy="86" r="8" fill={INK} />
          <circle cx="87" cy="83" r="3" fill={white} />
          <circle cx="119" cy="83" r="3" fill={white} />
          <path d="M70 74l3 5 5 3-5 3-3 5-3-5-5-3 5-3Z" fill="#f2b134" />
        </>
      );
    default:
      return (
        <>
          <circle cx="84" cy="86" r="6" fill={INK} />
          <circle cx="116" cy="86" r="6" fill={INK} />
        </>
      );
  }
}

function Mouth({ style }: { style: string }) {
  switch (style) {
    case 'grin':
      return (
        <path
          d="M80 104c4 10 12 16 20 16s16-6 20-16Z"
          fill="#8c3d3d"
          stroke={INK}
          strokeWidth="3"
          strokeLinejoin="round"
        />
      );
    case 'giggle':
      return (
        <>
          <path
            d="M80 104c4 12 12 18 20 18s16-6 20-18Z"
            fill="#8c3d3d"
            stroke={INK}
            strokeWidth="3"
          />
          <path d="M84 106h32" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" />
        </>
      );
    case 'calm':
      return <path d="M88 108h24" stroke={INK} strokeWidth="4" strokeLinecap="round" />;
    default:
      return (
        <path
          d="M84 104a17 17 0 0 0 32 0"
          fill="none"
          stroke={INK}
          strokeWidth="4"
          strokeLinecap="round"
        />
      );
  }
}

function Outfit({ kind, outfit }: { kind: string; outfit: { base: string; trim: string } }) {
  switch (kind) {
    case 'stripes':
      return (
        <>
          <path d="M42 200v-24a58 58 0 0 1 116 0v24Z" fill={outfit.base} />
          <path d="M44 168h112M42 184h116" stroke={outfit.trim} strokeWidth="9" />
        </>
      );
    case 'hoodie':
      return (
        <>
          <path d="M42 200v-24a58 58 0 0 1 116 0v24Z" fill={outfit.base} />
          <path
            d="M78 150c6 12 14 18 22 18s16-6 22-18l-8-6c-4 8-8 12-14 12s-10-4-14-12Z"
            fill={outfit.trim}
          />
          <path
            d="M96 168v18M104 168v18"
            stroke={outfit.trim}
            strokeWidth="4"
            strokeLinecap="round"
          />
        </>
      );
    case 'dress':
      return (
        <>
          <path d="M40 200l14-30a48 48 0 0 1 92 0l14 30Z" fill={outfit.base} />
          <path d="M62 178h76" stroke={outfit.trim} strokeWidth="7" />
        </>
      );
    case 'jersey':
      return (
        <>
          <path d="M42 200v-24a58 58 0 0 1 116 0v24Z" fill={outfit.base} />
          <path d="M42 176h116" stroke={outfit.trim} strokeWidth="10" />
          <circle cx="100" cy="188" r="12" fill={outfit.trim} />
        </>
      );
    case 'spacesuit':
      return (
        <>
          <path d="M42 200v-24a58 58 0 0 1 116 0v24Z" fill={outfit.base} />
          <circle cx="100" cy="180" r="14" fill={outfit.trim} />
          <path
            d="M56 170h18M126 170h18"
            stroke={outfit.trim}
            strokeWidth="8"
            strokeLinecap="round"
          />
        </>
      );
    default:
      return (
        <>
          <path d="M42 200v-24a58 58 0 0 1 116 0v24Z" fill={outfit.base} />
          <path
            d="M80 148c6 8 14 12 20 12s14-4 20-12"
            fill="none"
            stroke={outfit.trim}
            strokeWidth="6"
          />
        </>
      );
  }
}

function Accessory({ kind }: { kind: string }) {
  switch (kind) {
    case 'cap':
      return (
        <>
          <path d="M62 62a38 32 0 0 1 76 0Z" fill="#3f8f7d" />
          <path d="M136 62h26a7 7 0 0 1 0 11h-26Z" fill="#2f6f61" />
          <circle cx="100" cy="32" r="5" fill="#2f6f61" />
        </>
      );
    case 'headband':
      return <path d="M58 70h84" stroke="#e05f8a" strokeWidth="9" strokeLinecap="round" />;
    case 'glasses':
      return (
        <>
          <circle cx="84" cy="86" r="15" fill="none" stroke={INK} strokeWidth="4" />
          <circle cx="116" cy="86" r="15" fill="none" stroke={INK} strokeWidth="4" />
          <path
            d="M99 86h2M69 84l-11-4M131 84l11-4"
            stroke={INK}
            strokeWidth="4"
            strokeLinecap="round"
          />
        </>
      );
    case 'flower':
      return (
        <g transform="translate(132 56)">
          {[0, 72, 144, 216, 288].map((deg) => (
            <ellipse
              key={deg}
              cx="0"
              cy="-9"
              rx="6"
              ry="9"
              fill="#f0839f"
              transform={`rotate(${deg})`}
            />
          ))}
          <circle r="5" fill="#f2b134" />
        </g>
      );
    case 'snorkel':
      return (
        <>
          <rect
            x="66"
            y="74"
            width="68"
            height="26"
            rx="12"
            fill="#9ed3ea"
            opacity="0.75"
            stroke={INK}
            strokeWidth="3"
          />
          <path
            d="M134 82h10v34"
            fill="none"
            stroke="#e0714f"
            strokeWidth="7"
            strokeLinecap="round"
          />
        </>
      );
    default:
      return null;
  }
}
